import {
    ActionIcon,
    Anchor,
    Button,
    Group,
    Loader,
    NumberInput,
    Paper,
    Select,
    Stack,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {IconDownload, IconInfoCircle} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {parseApiDate} from 'src/jobs/job-schedule-format'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {downloadRentalReportCsv} from './export-rental-report-csv'
import {isStockRentalReportRow, type RentalReportRow} from './schemas'
import {
    parseDateInputValue,
    rentalReportDateSearchValue,
    type RentalReportSearch,
    resolveRentalReportSearch,
} from './search'
import {useRentalReport} from './use-rental-report'

type RentalReportPageProps = {
    search: RentalReportSearch
}

const STATUS_OPTIONS = [
    {value: 'deployed', label: 'Deployed'},
    {value: 'picked_up', label: 'Picked up'},
    {value: 'dispatched', label: 'Dispatched'},
]

function formatStatusLabel(status: RentalReportRow['status']) {
    const option = STATUS_OPTIONS.find((item) => item.value === status)
    return option?.label ?? status
}

function formatDeployedAt(deployedAt: Date) {
    return formatBusinessTimestamp(deployedAt, 'MM/dd/yy')
}

function daysOutColumnTitle() {
    return (
        <Group gap={4} wrap="nowrap">
            Days out
            <Tooltip
                label="Calendar days from the deployment date through the report end date. Both dates count as full days—for example, deployed Monday with end date Wednesday is 3 days."
                multiline
                w={280}
            >
                <ActionIcon
                    variant="transparent"
                    size="xs"
                    color="dimmed"
                    aria-label="How days out is calculated"
                    onClick={(event) => event.stopPropagation()}
                >
                    <IconInfoCircle size={14} stroke={1.5} />
                </ActionIcon>
            </Tooltip>
        </Group>
    )
}

function sortRows(rows: RentalReportRow[], sortStatus: DataTableSortStatus<RentalReportRow>) {
    const {columnAccessor, direction} = sortStatus
    const multiplier = direction === 'asc' ? 1 : -1

    return [...rows].sort((left, right) => {
        const leftValue = left[columnAccessor as keyof RentalReportRow]
        const rightValue = right[columnAccessor as keyof RentalReportRow]

        if (leftValue instanceof Date && rightValue instanceof Date) {
            return multiplier * (leftValue.getTime() - rightValue.getTime())
        }
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
            return multiplier * (leftValue - rightValue)
        }
        return multiplier * String(leftValue).localeCompare(String(rightValue))
    })
}

export function RentalReportPage({search}: RentalReportPageProps) {
    const navigate = useNavigate({from: '/reports/rental'})
    const {startDate, endDate} = resolveRentalReportSearch(search)
    const {data: report, isLoading, isError, error, refetch} = useRentalReport(startDate, endDate)

    const [assetFilter, setAssetFilter] = useState('')
    const [customerFilter, setCustomerFilter] = useState('')
    const [projectFilter, setProjectFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState<string | null>(null)
    const [quantityFilter, setQuantityFilter] = useState<string | number>('')
    const [daysOutFilter, setDaysOutFilter] = useState<string | number>('')

    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<RentalReportRow>>({
        columnAccessor: 'assetDisplayName',
        direction: 'asc',
    })

    function handleStartDateChange(value: string | Date | null) {
        const parsed = parseDateInputValue(value)
        if (!parsed) {
            return
        }
        void navigate({
            search: {
                start: rentalReportDateSearchValue(parsed),
                end: endDate,
            },
        })
    }

    function handleEndDateChange(value: string | Date | null) {
        const parsed = parseDateInputValue(value)
        if (!parsed) {
            return
        }
        void navigate({
            search: {
                start: startDate,
                end: rentalReportDateSearchValue(parsed),
            },
        })
    }

    const filteredRows = useMemo(() => {
        const rows = report?.rows ?? []
        return rows.filter((row) => {
            if (
                assetFilter &&
                !row.assetDisplayName.toLowerCase().includes(assetFilter.toLowerCase())
            ) {
                return false
            }
            if (
                customerFilter &&
                !row.customerName.toLowerCase().includes(customerFilter.toLowerCase())
            ) {
                return false
            }
            if (
                projectFilter &&
                !row.projectName.toLowerCase().includes(projectFilter.toLowerCase())
            ) {
                return false
            }
            if (
                typeFilter &&
                !row.rentalAssetTypeName.toLowerCase().includes(typeFilter.toLowerCase())
            ) {
                return false
            }
            if (statusFilter && row.status !== statusFilter) {
                return false
            }
            if (quantityFilter !== '' && row.quantity !== Number(quantityFilter)) {
                return false
            }
            if (daysOutFilter !== '' && row.daysOut !== Number(daysOutFilter)) {
                return false
            }
            return true
        })
    }, [
        report,
        assetFilter,
        customerFilter,
        projectFilter,
        typeFilter,
        statusFilter,
        quantityFilter,
        daysOutFilter,
    ])

    const sortedRows = useMemo(() => sortRows(filteredRows, sortStatus), [filteredRows, sortStatus])

    const filtersActive = Boolean(
        assetFilter ||
        customerFilter ||
        projectFilter ||
        typeFilter ||
        statusFilter ||
        quantityFilter !== '' ||
        daysOutFilter !== '',
    )

    const exportDisabled = isLoading || isError || !report || report.rows.length === 0

    const columns = useMemo((): DataTableColumn<RentalReportRow>[] => {
        return [
            {
                accessor: 'status',
                title: 'Status',
                sortable: true,
                render: ({status}) => formatStatusLabel(status),
                filter: (
                    <Select
                        label="Status"
                        placeholder="All"
                        data={STATUS_OPTIONS}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        clearable
                    />
                ),
                filtering: statusFilter != null,
            },
            {
                accessor: 'assetDisplayName',
                title: 'Asset',
                sortable: true,
                filter: (
                    <TextInput
                        label="Asset"
                        placeholder="Search assets..."
                        value={assetFilter}
                        onChange={(event) => setAssetFilter(event.currentTarget.value)}
                    />
                ),
                filtering: assetFilter !== '',
            },
            {
                accessor: 'rentalAssetTypeName',
                title: 'Type',
                sortable: true,
                filter: (
                    <TextInput
                        label="Type"
                        placeholder="Search types..."
                        value={typeFilter}
                        onChange={(event) => setTypeFilter(event.currentTarget.value)}
                    />
                ),
                filtering: typeFilter !== '',
            },
            {
                accessor: 'quantity',
                title: 'Quantity',
                sortable: true,
                filter: (
                    <NumberInput
                        label="Quantity"
                        placeholder="Filter by quantity"
                        value={quantityFilter}
                        onChange={setQuantityFilter}
                        min={1}
                    />
                ),
                filtering: quantityFilter !== '',
            },
            {
                accessor: 'customerName',
                title: 'Customer',
                sortable: true,
                filter: (
                    <TextInput
                        label="Customer"
                        placeholder="Search customers..."
                        value={customerFilter}
                        onChange={(event) => setCustomerFilter(event.currentTarget.value)}
                    />
                ),
                filtering: customerFilter !== '',
            },
            {
                accessor: 'projectName',
                title: 'Order',
                sortable: true,
                render: ({projectId, projectName}) => (
                    <Anchor
                        component={Link}
                        to="/projects/$projectId"
                        params={{projectId}}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {projectName}
                    </Anchor>
                ),
                filter: (
                    <TextInput
                        label="Order"
                        placeholder="Search orders..."
                        value={projectFilter}
                        onChange={(event) => setProjectFilter(event.currentTarget.value)}
                    />
                ),
                filtering: projectFilter !== '',
            },
            {
                accessor: 'locationLabel',
                title: 'Location',
                sortable: true,
            },
            {
                accessor: 'daysOut',
                title: daysOutColumnTitle(),
                sortable: true,
                filter: (
                    <NumberInput
                        label="Days out"
                        placeholder="Filter by days out"
                        value={daysOutFilter}
                        onChange={setDaysOutFilter}
                        min={0}
                    />
                ),
                filtering: daysOutFilter !== '',
            },
            {
                accessor: 'deployedAt',
                title: 'Deployed date',
                sortable: true,
                render: ({deployedAt}) => formatDeployedAt(deployedAt),
            },
        ]
    }, [
        assetFilter,
        customerFilter,
        projectFilter,
        typeFilter,
        statusFilter,
        quantityFilter,
        daysOutFilter,
    ])

    if (isLoading) {
        return (
            <Stack gap="md">
                <RentalReportHeader
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    exportDisabled
                    onExport={() => {}}
                />
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            </Stack>
        )
    }

    if (isError || !report) {
        const message = error instanceof Error ? error.message : 'Failed to load rental report'
        return (
            <Stack gap="md">
                <RentalReportHeader
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    exportDisabled
                    onExport={() => {}}
                />
                <Paper withBorder p="md" radius="md">
                    <Title order={3} size="h4" c="red">
                        Could not load rental report
                    </Title>
                    <p>{message}</p>
                </Paper>
                <Button onClick={() => void refetch()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    return (
        <Stack gap="md">
            <RentalReportHeader
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                exportDisabled={exportDisabled}
                onExport={() => downloadRentalReportCsv(report)}
            />

            <Paper withBorder radius="md" style={{overflowX: 'auto'}}>
                <DataTable
                    withTableBorder
                    withColumnBorders
                    striped
                    highlightOnHover
                    textSelectionDisabled
                    records={sortedRows}
                    columns={columns}
                    sortStatus={sortStatus}
                    onSortStatusChange={setSortStatus}
                    fetching={isLoading}
                    minHeight={sortedRows.length === 0 ? 180 : undefined}
                    noRecordsText={
                        filtersActive
                            ? 'No rental assets match your filters.'
                            : 'No rental assets on rent for the selected range.'
                    }
                    onRowClick={({record}) => {
                        if (isStockRentalReportRow(record)) {
                            void navigate({
                                to: '/rental-assets/stock/$typeId',
                                params: {typeId: record.rentalAssetTypeId},
                            })
                            return
                        }

                        void navigate({
                            to: '/rental-assets/$assetId',
                            params: {assetId: record.rentalAssetId},
                        })
                    }}
                />
            </Paper>
        </Stack>
    )
}

type RentalReportHeaderProps = {
    startDate: string
    endDate: string
    onStartDateChange: (value: string | Date | null) => void
    onEndDateChange: (value: string | Date | null) => void
    exportDisabled: boolean
    onExport: () => void
}

function RentalReportHeader({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    exportDisabled,
    onExport,
}: RentalReportHeaderProps) {
    return (
        <Stack gap={4}>
            <DetailPageBackLink
                to="/rental-assets"
                listLabel="equipment"
                backLabel="Back to equipment"
            />
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                <Group gap="xs" align="center" wrap="wrap">
                    <Title order={1}>Rental Report</Title>
                    <DateInput
                        label="Start"
                        value={parseApiDate(startDate)}
                        onChange={onStartDateChange}
                        valueFormat="MM/DD/YY"
                    />
                    <DateInput
                        label="End"
                        value={parseApiDate(endDate)}
                        onChange={onEndDateChange}
                        valueFormat="MM/DD/YY"
                    />
                </Group>
                <Button
                    variant="default"
                    leftSection={<IconDownload size={16} stroke={1.5} />}
                    disabled={exportDisabled}
                    onClick={onExport}
                >
                    Export CSV
                </Button>
            </Group>
        </Stack>
    )
}
